import { useEffect, useState } from "react";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import {
  DropDownList,
  DropDownListChangeEvent,
  MultiSelect,
  MultiSelectChangeEvent,
} from "@progress/kendo-react-dropdowns";
import SpinbuttonDateInput from "@/components/shared/forms/SpinbuttonDateInput";
import { defaultProgramYear } from "@/utils/dates";
import { accessibleNoDataRender } from "@/components/shared/forms/kendoAccessibleNoData";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

type PreschoolSummary = {
  PreschoolCDSCode: string;
  PreschoolName: string;
};

export type ChildSearchFormState = {
  programYear: string;
  schools: PreschoolSummary[];
  dateOfBirth: string;
  firstName: string;
  middleName: string;
  lastName: string;
};

type ChildSearchFormProps = {
  programYearOptions: string[];
  preschools: PreschoolSummary[];
  disabled: boolean;
  onSearch: (filters: ChildSearchFormState) => void;

  initialProgramYear?: string;

  onClear?: () => void;
};

export function ChildSearchForm({
  programYearOptions,
  preschools,
  disabled,
  onSearch,
  initialProgramYear,
  onClear,
}: ChildSearchFormProps) {
  const [formState, setFormState] = useState<ChildSearchFormState>(() => ({
    programYear: defaultProgramYear(initialProgramYear),
    schools: [],
    dateOfBirth: "",
    firstName: "",
    middleName: "",
    lastName: "",
  }));
  const [dobResetKey, setDobResetKey] = useState(0);

  // See MultiSelectToggleButton: gives keyboard users a way to browse the
  // School list without having to type first.
  const [schoolsOpened, setSchoolsOpened] = useState(false);

  useMultiSelectChipListLabel(
    "child-search-school",
    "School",
    formState.schools
  );
  useMultiSelectListboxMultiselectable("child-search-school", schoolsOpened);

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      programYear: defaultProgramYear(initialProgramYear),
    }));
  }, [initialProgramYear]);

  const handleChange = (key: keyof ChildSearchFormState, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSearchClick = () => {
    if (!disabled) onSearch(formState);
  };

  const handleClearClick = () => {
    const cleared: ChildSearchFormState = {
      programYear: defaultProgramYear(),
      schools: [],
      dateOfBirth: "",
      firstName: "",
      middleName: "",
      lastName: "",
    };

    setFormState(cleared);
    setDobResetKey((k) => k + 1);

    if (!disabled) onSearch(cleared);

    onClear?.();
  };

  return (
    <div className="mb-0 p-4 bg-white w-100 child-search-form">
      <div className="row g-3">
        {/* Program Year */}
        <div className="col-12 col-md-4">
          <span
            id="child-search-program-year-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-calendar me-1" aria-hidden="true"></i> Program
            Year
          </span>
          <DropDownList
            id="child-search-program-year"
            ariaLabelledBy="child-search-program-year-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={programYearOptions}
            value={formState.programYear}
            onChange={(e: DropDownListChangeEvent) => {
              // Kendo's DropDownList can fire onChange with an empty/undefined
              // value during keyboard navigation (arrowing through the open
              // popup), before it settles on the actually-highlighted item.
              // Writing that straight into state blanks the displayed value
              // and it stays blank, since nothing re-derives it afterward.
              if (e.value) handleChange("programYear", e.value);
            }}
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender(
              "No program years available"
            )}
          />
        </div>

        {/* School (MultiSelect) */}
        <div className="col-12 col-md-4">
          <span
            id="child-search-school-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-building me-1" aria-hidden="true"></i> School
          </span>
          <MultiSelect
            id="child-search-school"
            ariaLabelledBy="child-search-school-label"
            className="child-search-control"
            style={{ width: "100%" }}
            data={preschools}
            textField="PreschoolName"
            dataItemKey="PreschoolCDSCode"
            value={formState.schools}
            onChange={(e: MultiSelectChangeEvent) =>
              handleChange("schools", e.value as PreschoolSummary[])
            }
            placeholder="All schools"
            disabled={disabled}
            listNoDataRender={accessibleNoDataRender("No schools available")}
            opened={schoolsOpened}
            onOpen={() => setSchoolsOpened(true)}
            onClose={() => setSchoolsOpened(false)}
            itemRender={multiSelectSelectedItemRender}
            popupSettings={{ className: "child-search-school-listbox-popup" }}
            tagRender={multiSelectTagRender}
            suffix={
              <>
                <MultiSelectToggleButton
                  opened={schoolsOpened}
                  onToggle={() => setSchoolsOpened((prev) => !prev)}
                  disabled={disabled}
                />
                {formState.schools.length > 0 && (
                  <MultiSelectClearButton
                    onClear={() => handleChange("schools", [])}
                    disabled={disabled}
                  />
                )}
              </>
            }
          />
        </div>

        {/* Date of Birth */}
        <div className="col-12 col-md-4">
          <label
            id="child-search-dob-label"
            className="form-label fw-semibold"
          >
            <i className="bi bi-calendar-event me-1" aria-hidden="true"></i>{" "}
            Date of Birth
            <span className="visually-hidden"> format mm/dd/yyyy</span>
          </label>
          <SpinbuttonDateInput
            key={dobResetKey}
            id="child-search-dob"
            labelledBy="child-search-dob-label"
            className="child-search-control"
            style={{ width: "100%" }}
            placeholderColor="#9ca3af"
            fontSize="14px"
            value={formState.dateOfBirth}
            onChange={(value) => handleChange("dateOfBirth", value)}
            disabled={disabled}
          />
        </div>

        {/* Child First Name */}
        <div className="col-12 col-md-4">
          <label
            id="child-search-first-name-label"
            htmlFor="child-search-first-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> Child First
            Name
          </label>
          <Input
            id="child-search-first-name"
            ariaLabelledBy="child-search-first-name-label"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.firstName}
            onChange={(e: InputChangeEvent) =>
              handleChange("firstName", e.value)
            }
            placeholder="Enter first name"
            disabled={disabled}
          />
        </div>

        {/* Child Middle Name */}
        <div className="col-12 col-md-4">
          <label
            id="child-search-middle-name-label"
            htmlFor="child-search-middle-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> Child
            Middle Name
          </label>
          <Input
            id="child-search-middle-name"
            ariaLabelledBy="child-search-middle-name-label"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.middleName}
            onChange={(e: InputChangeEvent) =>
              handleChange("middleName", e.value)
            }
            placeholder="Enter middle name"
            disabled={disabled}
          />
        </div>

        {/* Child Last Name */}
        <div className="col-12 col-md-4">
          <label
            id="child-search-last-name-label"
            htmlFor="child-search-last-name"
            className="form-label fw-semibold"
          >
            <i className="bi bi-person me-1" aria-hidden="true"></i> Child Last
            Name
          </label>
          <Input
            id="child-search-last-name"
            ariaLabelledBy="child-search-last-name-label"
            className="child-search-control"
            style={{ width: "100%" }}
            value={formState.lastName}
            onChange={(e: InputChangeEvent) =>
              handleChange("lastName", e.value)
            }
            placeholder="Enter last name"
            disabled={disabled}
          />
        </div>

        {/* Buttons */}
        <div className="col-12 d-flex align-items-end gap-2 mt-4 mb-1">
          <button
            type="button"
            className="btn btn-primary d-flex align-items-center gap-2"
            onClick={handleSearchClick}
            disabled={disabled}
          >
            <i className="bi bi-search" aria-hidden="true"></i>
            Search
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClearClick}
            disabled={disabled}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}
